<template>
  <div v-if="props.show" class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto flex items-center justify-center text-base">
    <div class="bg-white rounded-lg shadow-xl w-59/12 h-[70vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-lg text-gray-800 font-bold">Add Beneficiaries</h4>
        <button
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
          type="button"
          @click="emitClose"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <svg
            class="w-3 h-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="px-6 py-4">
        <div v-for="(beneficiary, index) in beneficiaries" :key="index" class="grid grid-cols-5 gap-x-6 gap-y-6 mb-4">
          <!-- Beneficiary Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700">
              Name<span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              v-model="beneficiary.name"
              placeholder="Enter name"
              class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            />
            <span class="text-xs text-red-500" v-if="beneficiary.errors.name">{{ beneficiary.errors.name }}</span>
          </div>
          <!-- ID Number -->
          <div>
            <label class="block text-sm font-medium text-gray-700">
              ID Number<span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              v-model="beneficiary.id_number"
              placeholder="Enter ID number"
              class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            />
            <span class="text-xs text-red-500" v-if="beneficiary.errors.id_number">{{ beneficiary.errors.id_number }}</span>
          </div>
          <!-- Cover Amount -->
          <div>
            <label class="block text-sm font-medium text-gray-700">
              Cover (KES)<span class="text-red-500">*</span>
            </label>
            <input
              type="number"
              v-model="beneficiary.cover_amount"
              min="0"
              step="0.01"
              placeholder="Enter cover amount"
              class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            />
            <span class="text-xs text-red-500" v-if="beneficiary.errors.cover_amount">{{ beneficiary.errors.cover_amount }}</span>
          </div>
          <!-- Validity End Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700">
              Valid Until<span class="text-red-500">*</span>
            </label>
            <input
              type="date"
              v-model="beneficiary.validity_end_date"
              :min="minDate"
              class="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            />
            <span class="text-xs text-red-500" v-if="beneficiary.errors.validity_end_date">{{ beneficiary.errors.validity_end_date }}</span>
          </div>
          <!-- Actions -->
          <div class="flex items-end">
            <div class="flex space-x-4" v-if="index === beneficiaries.length - 1">
              <svg
                @click="addBeneficiaryRow"
                class="text-gray-800 cursor-pointer"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="yellow"
                viewBox="0 0 18 18"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 12h14m-7 7V5"
                />
              </svg>
              <svg
                v-if="beneficiaries.length > 1"
                @click="deleteBeneficiaryRow(index)"
                class="text-red-500 cursor-pointer"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
        </div>
        <!-- Buttons -->
        <div class="flex space-x-4 mt-4">
          <CustomButton
            label="Cancel"
            bg-class="bg-gray-200 text-gray-800 hover:bg-gray-300"
            @click="emitClose"
          />
          <CustomButton
            v-if="!isLoading"
            label="Submit"
            bg-class="bg-custom-yellow text-white hover:bg-yellow-600"
            @click="handleSubmit"
          />
          <CustomButton
            v-else
            label="Submitting..."
            bg-class="bg-custom-yellow text-white"
            disabled
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'
import { useToast } from 'vue-toast-notification'
import CustomButton from '@/components/CustomButton.vue'
import apiClient from '@/services/api'


const props = defineProps({
  show: { type: Boolean, default: false, required: true },
  companyId: { type: String, required: true }
})

const emits = defineEmits(['close', 'add-success'])

const toast = useToast();
const isLoading = ref(false);

const beneficiaries = ref([
  {
    name: '',
    id_number: '',
    cover_amount: '',
    validity_end_date: '',
    errors: {}
  }
])

const minDate = new Date().toISOString().split('T')[0]

const validateForm = () => {
  let isValid = true
  beneficiaries.value.forEach((beneficiary) => {
    beneficiary.errors = {}

    if (!beneficiary.name) {
      beneficiary.errors.name = 'Beneficiary name must be provided.'
      isValid = false
    }

    if (!beneficiary.id_number) {
      beneficiary.errors.id_number = 'ID Number is must be provided.';
      isValid = false;

    } else if (beneficiary.id_number && beneficiary.id_number.length < 8) {
      beneficiary.errors.id_number = 'ID Number must be at least 8 digits.';
      isValid = false;
    }

    if (!beneficiary.cover_amount) {
      beneficiary.errors.id_number = 'FFS Cover amount must be provided.';
      isValid = false;

    } else if (beneficiary.cover_amount <= 0) {
      beneficiary.errors.cover_amount = 'Cover amount must be greater than KES O.00.';
      isValid = false;
    }

    if (!beneficiary.validity_end_date) {
      beneficiary.errors.validity_end_date = 'Validity end date must be set.'
      isValid = false

    } else if (new Date(beneficiary.validity_end_date) < new Date(minDate)) {
      beneficiary.errors.validity_end_date = 'Validity end date must be a future date from today.'
      isValid = false
    }

  })
  return isValid;
}

const addBeneficiaryRow = () => {
  console.log('are you?')
  if (!validateForm()) return;

  beneficiaries.value.push({
    name: '',
    id_number: '',
    cover_amount: null,
    validity_end_date: '',
    errors: {}
  });
}

const deleteBeneficiaryRow = (index) => {
  beneficiaries.value.splice(index, 1)
}

const emitClose = () => {
  beneficiaries.value = [{
    name: '',
    id_number: '',
    cover_amount: null,
    validity_end_date: '',
    errors: {}
  }]

  emits('close')
}

const handleSubmit = async () => {
  if (!validateForm) return;

  isLoading.value = true;

  try {
    const payload = {
      company_id: props.companyId,
      beneficiaries: beneficiaries.value.map((beneficiary) => ({
        name: beneficiary.name.trim(),
        id_number: beneficiary.id_number.trim(),
        cover_amount: parseFloat(beneficiary.cover_amount),
        validity_end_date: beneficiary.validity_end_date
      }))
    }

    console.log('payload', payload)

    const response = await apiClient.addCompanyBeneficiaries(props.companyId, payload)

    if (response.success) {
      toast.success('Beneficiaries added successfully.')
      emits('add-success');
      emitClose();

    } else {
      toast.error(response.message || response.errors?.join(', ') || 'Failed to add beneficiaries.')
    }

  } catch (error) {
    toast.error(error.message || 'Failed to add beneficiaries.')
    console.log('Failed to add beneficiaries', error)
  } finally {
    isLoading.value = false
  }
}
</script>